import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '@/services/api';

const AuthContext = createContext(null);

// SECURITY: Session tokens are NEVER stored in localStorage.
// Authentication is driven by httpOnly + secure cookies set by the backend
// (see /app/backend/routes/auth.py — `response.set_cookie(httponly=True, ...)`).
// We only cache non-sensitive UX data (user profile + feature flags) for
// cross-tab continuity and to avoid logged-out flashes.
const USER_STORAGE_KEY = 'harmonix_user';
const FEATURE_ACCESS_KEY = 'harmonix_features';
const LEGACY_SESSION_KEY = 'harmonix_session';  // removed; cleared on boot

// One-time cleanup — purge any legacy session token from older builds.
try { localStorage.removeItem(LEGACY_SESSION_KEY); } catch { /* ignore */ }

const safeGet = (key) => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key, val) => {
  try {
    if (val == null) localStorage.removeItem(key);
    else localStorage.setItem(key, val);
  } catch { /* ignore */ }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const authCheckPerformed = useRef(false);

  // Initialize user from cached profile (non-sensitive)
  const [user, setUser] = useState(() => {
    try {
      const stored = safeGet(USER_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  // We no longer have a token to gate "is loading" with. Instead, if we have
  // cached user data we render optimistically and revalidate via /auth/me
  // (cookie is automatically attached). If revalidation fails, we'll clear.
  const [loading, setLoading] = useState(() => !safeGet(USER_STORAGE_KEY));
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!safeGet(USER_STORAGE_KEY));
  const [featureAccess, setFeatureAccess] = useState(() => {
    try {
      const stored = safeGet(FEATURE_ACCESS_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const [permissions, setPermissions] = useState(null); // {segment: {read,write,edit,delete}}

  // Persist user (non-sensitive) for cross-tab UX
  useEffect(() => {
    if (user) safeSet(USER_STORAGE_KEY, JSON.stringify(user));
    else safeSet(USER_STORAGE_KEY, null);
  }, [user]);

  // Persist feature flags (non-sensitive)
  useEffect(() => {
    if (featureAccess) safeSet(FEATURE_ACCESS_KEY, JSON.stringify(featureAccess));
    else safeSet(FEATURE_ACCESS_KEY, null);
  }, [featureAccess]);

  const fetchFeatureAccess = useCallback(async () => {
    try {
      const response = await authAPI.getFeatureAccess();
      setFeatureAccess(response.data.feature_access);
    } catch (error) {
      console.error('Failed to fetch feature access:', error);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await authAPI.getMyPermissions();
      setPermissions(response.data.permissions || {});
    } catch (error) {
      // Non-authenticated users won't have permissions — ignore silently
      setPermissions(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      // The browser will attach the httpOnly cookie automatically.
      // Just call /auth/me — if cookie is valid we get a 200, else 401.
      const response = await authAPI.getMe();
      setUser(response.data);
      setIsAuthenticated(true);
      fetchFeatureAccess();
      fetchPermissions();
    } catch (error) {
      if (error.response?.status === 401) {
        setUser(null);
        setIsAuthenticated(false);
        setFeatureAccess(null);
        setPermissions(null);
        safeSet(USER_STORAGE_KEY, null);
        safeSet(FEATURE_ACCESS_KEY, null);
      }
      // Network errors etc. — keep cached user (offline-friendly)
    } finally {
      setLoading(false);
    }
  }, [fetchFeatureAccess, fetchPermissions]);

  // Cross-tab sync — listen for user-cache changes (token no longer in storage)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === USER_STORAGE_KEY) {
        if (e.newValue) {
          try {
            setUser(JSON.parse(e.newValue));
            setIsAuthenticated(true);
          } catch { /* ignore */ }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    // Skip initial auth check if we're handling OAuth callback
    const hasSessionId = window.location.hash?.includes('session_id=');
    if (hasSessionId) {
      setLoading(false);
      return;
    }
    if (!authCheckPerformed.current) {
      authCheckPerformed.current = true;
      checkAuth();
    }
  }, [checkAuth]);

  const login = async (sessionId) => {
    try {
      const response = await authAPI.exchangeSession(sessionId);
      // Backend has set the httpOnly session_token cookie via Set-Cookie.
      setUser(response.data);
      setIsAuthenticated(true);
      fetchFeatureAccess();
      fetchPermissions();
      return response.data;
    } catch (error) {
      console.error('[Auth] Google OAuth error:', error);
      throw error;
    }
  };

  const loginWithEmail = async (email, password) => {
    const response = await authAPI.login(email, password);
    // Backend has set the httpOnly cookie. Cache user for UX.
    setUser(response.data);
    setIsAuthenticated(true);
    fetchFeatureAccess();
    fetchPermissions();
    return response.data;
  };

  const register = async (name, email, password, accountType = 'customer', referralCode = null) => {
    const response = await authAPI.register(name, email, password, accountType, referralCode);
    setUser(response.data);
    setIsAuthenticated(true);
    fetchFeatureAccess();
    fetchPermissions();
    return response.data;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setFeatureAccess(null);
      setPermissions(null);
      safeSet(USER_STORAGE_KEY, null);
      safeSet(FEATURE_ACCESS_KEY, null);
    }
  };

  const hasFeature = useCallback((feature) => {
    if (user?.role === 'system_admin') return true;
    return featureAccess?.[feature] ?? false;
  }, [user, featureAccess]);

  // RBAC helper — checks if current user has `action` on `segment`.
  // system_admin always returns true. While permissions are still loading
  // for system_admin (cached profile only) we also return true to avoid
  // hiding admin UI on first paint.
  const hasPermission = useCallback((segment, action = 'read') => {
    if (user?.role === 'system_admin') return true;
    if (!permissions) return false;
    return Boolean(permissions?.[segment]?.[action]);
  }, [user, permissions]);

  const value = {
    user,
    loading,
    isAuthenticated,
    featureAccess,
    permissions,
    hasFeature,
    hasPermission,
    login,
    loginWithEmail,
    register,
    logout,
    checkAuth,
    fetchFeatureAccess,
    fetchPermissions,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
